import axios from 'axios';
import { gaeApp } from '../components/helpers/Analytics';
import { removeCachedTemplates } from './templateCache';
import { API } from '@aws-amplify/api';
import { isAppOnline } from './connectivity';

export { isAppOnline } from './connectivity';

export const REQUEST_TYPE = {
  COMMON_REQUEST: '/r=',
  DEMO_TEMPLATE: '/d=',
  TEMPLATE: '/t=',
  SIGN_NOW: '/s=',
  FILE_DOWNLOAD: '/f=',
  CROSS_DEVICE_DOCUMENT: '/do=',
  PREVIEW: '/preview='
};

const DEMO_REQUEST_ID = 'Us7L8RGYzYx2SZ0Qs1masvlpQsDen5CUS2zY';

export function extractDemoRequestId(requestId) {
  return requestId === 'demo' ? DEMO_REQUEST_ID : requestId;
}

export const setCookie = (cname, cvalue, exdays) => {
  let d = new Date();
  exdays && d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = exdays ? 'expires=' + d.toUTCString() : '';
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
};

export const setRequestIdCookie = requestId => {
  setCookie('recipientRequestId', requestId, 1);
};

export const setLastUsedRequestIdCookie = requestId => {
  setCookie('lastRecipientRequestId', requestId, 999);
};

export const resetRequestIdCookie = () => {
  setCookie('recipientRequestId', '');
};

export function isRequestIdADemoRequest(requestId) {
  return requestId === 'demo';
}

export function isRequestInTestMode(requestInfo) {
  return requestInfo.params?.testMode === 'true';
}

export function isRequestACrossDeviceDocument(requestInfo) {
  return requestInfo.requestType === REQUEST_TYPE.CROSS_DEVICE_DOCUMENT;
}

export function saveTemplatesUserData(userName, userEmail, userFields) {
  /*const rawData = getTemplatesUserData();
  const currentUserFields = rawData && rawData.userFields || [];
  userFields.forEach(field => currentUserFields)*/
  setCookie(
    'lastTemplateUserData',
    JSON.stringify({
      userName,
      userEmail,
      userFields
    }),
    300
  );
}

export function getTemplatesUserData(requestedUserFieldsStructure) {
  const rawData = getCookie('lastTemplateUserData');

  if (rawData) {
    const savedData = JSON.parse(rawData);
    if (Array.isArray(savedData.userFields)) {
      savedData.userFields.forEach(savedField => {
        const index = requestedUserFieldsStructure.findIndex(
          item => item.id === savedField.id
        );
        if (index !== -1) {
          requestedUserFieldsStructure[index].value = savedField.value;
        }
      });
    }
    savedData.userFields = requestedUserFieldsStructure;
    return savedData;
  }
  return null;
}

export const getRequestIdFromCookie = () => getCookie('recipientRequestId');
export const getLastUsedRequestIdFromCookie = () =>
  getCookie('lastRecipientRequestId');

/**
 * getRequestInfoFromUrl
 *
 * @param {string} _url=''
 * @returns {{}|{requestType: string, requestId: string}}
 */
export const getRequestInfoFromUrl = _url => {
  const url = _url || window.location.href;

  for (const [, requestType] of Object.entries(REQUEST_TYPE)) {
    const splitRequestUrl = url.split(requestType);
    if (splitRequestUrl.length > 1) {
      const splitRequestIdStr = splitRequestUrl[1].split('&');
      return {
        requestId: splitRequestIdStr[0],
        params: queryStringToJSON(splitRequestIdStr.slice(1)),
        isRequestIdADemoRequest: isRequestIdADemoRequest(splitRequestIdStr[0]),
        requestType
      };
    }
  }
  return {};
};

function queryStringToJSON(pairs) {
  if (!Array.isArray(pairs)) return {};

  const result = {};
  pairs.forEach(function(p) {
    const pair = p.split('=');
    const key = pair[0];
    const value = decodeURIComponent(pair[1] || '');

    if (result[key]) {
      if (Object.prototype.toString.call(result[key]) === '[object Array]') {
        result[key].push(value);
      } else {
        result[key] = [result[key], value];
      }
    } else {
      result[key] = value;
    }
  });

  return JSON.parse(JSON.stringify(result));
}

export const getCookie = (cname, _cookie) => {
  if (navigator.cookieEnabled) {
    const name = cname + '=';
    const cookie = _cookie || document.cookie;
    let ca = cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
  }
  return null;
};

export const getUsedTemplates = () => {
  const usedTemplates = getCookie('usedTemplates');
  try {
    return usedTemplates ? JSON.parse(decodeURI(usedTemplates)) : [];
  } catch (e) {
    return [];
  }
};

export const setUsedTemplates = usedTemplates => {
  setCookie('usedTemplates', encodeURI(JSON.stringify(usedTemplates)), 9999);
};

export const updateUsedTemplates = (id, name) => {
  const usedTemplatesArr = getUsedTemplates();
  if (usedTemplatesArr.some(item => item.id === id)) return;

  setUsedTemplates([
    {
      id,
      name,
      logo: ''
    },
    ...usedTemplatesArr.map(item => ({ ...item, logo: '' }))
  ]);
};

export const removeUsedTemplates = ids => {
  const usedTemplatesArr = getUsedTemplates();

  setUsedTemplates(usedTemplatesArr.filter(item => !ids.includes(item.id)));
  removeCachedTemplates(ids);
};

export const getIgnoredTemplates = () => {
  const ignoredTemplates = getCookie('ignoredTemplates');
  try {
    return ignoredTemplates ? JSON.parse(decodeURI(ignoredTemplates)) : [];
  } catch (e) {
    return [];
  }
};

export const isTemplateIgnored = id => {
  if (!id) return false;
  return getIgnoredTemplates().includes(id);
};

export const addIgnoredTemplate = id => {
  if (!id || isTemplateIgnored(id)) return;
  setCookie(
    'ignoredTemplates',
    encodeURI(JSON.stringify([...getIgnoredTemplates(), id])),
    9999
  );
};

export const getBrowserVersion = function() {
  const ua = navigator.userAgent;
  let tem;
  let M =
    ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) ||
    [];
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return 'IE ' + (tem[1] || '');
  }
  if (M[1] === 'Chrome') {
    tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
    if (tem != null)
      return tem
        .slice(1)
        .join(' ')
        .replace('OPR', 'Opera');
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
  if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);

  return M.join(' ');
};

export const isDesktopMode = () => {
  return window.innerWidth > window.screen.availWidth;
};

export const isTouchDevice = () => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
};

export const isMobileOrTablet = function() {
  let check = false;
  (function(a) {
    // noinspection RegExpSingleCharAlternation,RegExpRedundantEscape
    if (
      //eslint-disable-next-line
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      //eslint-disable-next-line
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);

  return (
    check ||
    (/Macintosh/i.test(navigator.userAgent) &&
      navigator.maxTouchPoints &&
      navigator.maxTouchPoints > 1)
  );
};

let prepareAppInFlight = null;

export function prepareApp(doOnDone = () => {}, doOnError = () => {}) {
  if (!isAppOnline()) {
    doOnDone();
    return;
  }

  if (window.appPreparedOnline) {
    doOnDone();
    return;
  }

  if (prepareAppInFlight) {
    prepareAppInFlight.then(doOnDone, doOnError);
    return;
  }

  const base = process.env.REACT_APP_EXPRESS_SERVER_URL || '';

  const loadScript = src =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });

  prepareAppInFlight = new Promise((resolve, reject) => {
    const runAuth = () => {
      if (process.env.REACT_APP_AUTH20_ENABLED === 'true') {
        const oauth = require('axios-oauth-client');

        if (!window.ff4d4374296c49aebbd1ad3f795af44c) {
          reject(new Error('AUTH_CONFIG_MISSING'));
          return;
        }
        // eslint-disable-next-line no-new-func
        const prepare = new Function(
          'axios',
          'oauth',
          'onDone',
          atob(window.ff4d4374296c49aebbd1ad3f795af44c)
        );

        gaeApp('START_REFRESH_TOKEN_CRONJOB');
        window.lastRefreshToken = Date.now();
        window.tokenRefresh = () => {
          window.lastRefreshToken = Date.now();
          const { runInPollDrop } = require('../telemetry/pollQuiet');
          runInPollDrop(() => {
            prepare(axios, oauth, error => {
              gaeApp(
                'REFRESH_TOKEN',
                error ? 'Error on refresh token' : 'Token successfuly refreshed'
              );
            });
          });
        };

        if (!window.tokenRefreshId) {
          window.tokenRefreshId = setInterval(
            window.tokenRefresh,
            59 * 60 * 1000
          );
        }

        prepare(axios, oauth, error => {
          if (error) {
            reject(error);
          } else {
            window.appPreparedOnline = true;
            resolve();
          }
        });
      } else {
        window.appPreparedOnline = true;
        resolve();
      }
    };

    const afterEnv = () => {
      if (
        process.env.REACT_APP_ENV !== 'local' &&
        window.__RUNTIME_ENV__?.REACT_APP_API_GATEWAY_ENDPOINT
      ) {
        API.configure({
          endpoints: [
            {
              name: process.env.REACT_APP_AWS_API_GATEWAY_NAME,
              endpoint: window.__RUNTIME_ENV__.REACT_APP_API_GATEWAY_ENDPOINT
            }
          ]
        });
      }

      if (process.env.REACT_APP_AUTH20_ENABLED === 'true') {
        return loadScript(`${base}/config`).then(runAuth);
      }
      runAuth();
    };

    // Env/config may already be present after a previous partial attempt.
    if (window.__RUNTIME_ENV__) {
      Promise.resolve()
        .then(afterEnv)
        .catch(reject);
      return;
    }

    loadScript(`${base}/env`)
      .then(afterEnv)
      .catch(reject);
  }).then(
    value => {
      prepareAppInFlight = null;
      return value;
    },
    error => {
      prepareAppInFlight = null;
      return Promise.reject(error);
    }
  );

  prepareAppInFlight.then(doOnDone, doOnError);
}

/** Fired after connectivity is online and prepareApp finished successfully. */
export const APP_ONLINE_EVENT = 'paperfly-app-online';
export const APP_OFFLINE_EVENT = 'paperfly-app-offline';

let onlinePrepareListenerInstalled = false;

/**
 * Global online/offline handling: when the app comes online, run prepareApp
 * (env/config/auth) so API calls work from any screen.
 */
export function initOnlinePrepareListener() {
  if (onlinePrepareListenerInstalled || typeof window === 'undefined') {
    return;
  }
  onlinePrepareListenerInstalled = true;

  window.addEventListener('online', () => {
    gaeApp('CONNECTIVITY.ONLINE');
    prepareApp(
      () => {
        if (typeof window.tokenRefresh === 'function') {
          window.tokenRefresh();
        }
        window.dispatchEvent(new CustomEvent(APP_ONLINE_EVENT));
      },
      () => {
        gaeApp('CONNECTIVITY.ONLINE_PREPARE_FAILED');
      }
    );
  });

  window.addEventListener('offline', () => {
    gaeApp('CONNECTIVITY.OFFLINE');
    window.dispatchEvent(new CustomEvent(APP_OFFLINE_EVENT));
  });
}

export function getCurrentHost() {
  return window.location.protocol + '//' + window.location.host;
}

export function prepareManifestUrl() {
  const host = getCurrentHost();
  const requestInfo = getRequestInfoFromUrl();

  const manifest = {
    short_name: 'Paperfly',
    name: 'Paperfly',
    description: 'Paperfly App',
    orientation: 'portrait',
    icons: [
      {
        src: 'favicon512.png',
        sizes: '72x72 96x96 128x128 256x256 512x512',
        type: 'image/png'
      }
    ],
    start_url: host + requestInfo.requestType + requestInfo.requestId,
    display: 'standalone',
    background_color: '#ffffff'
  };

  const content = encodeURIComponent(JSON.stringify(manifest));
  const url = 'data:application/manifest+json,' + content;

  document.querySelector('#manifestLink').setAttribute('href', url);
}

export const getSystemInfo = f7 => {
  return {
    os: f7.device.os,
    osVersion: f7.device.osVersion,
    device: f7.device.iphone
      ? 'iPhone'
      : f7.device.ipad
      ? 'iPad'
      : f7.device.android
      ? 'Android'
      : f7.device.desktop
      ? 'Desktop'
      : 'Another',
    browser: window.isChrome
      ? 'Chrome'
      : window.isSafari
      ? 'Safari'
      : f7.device.ie
      ? 'InternetExplorer'
      : f7.device.edge
      ? 'Edge'
      : 'Another'
  };
};

export const getSystemInfoLog = f7 => {
  const si = getSystemInfo(f7);
  return (
    'OS: ' +
    si.os +
    '\r\n' +
    'OS_VERSION: ' +
    si.osVersion +
    '\r\n' +
    'BROWSER: ' +
    si.browser +
    '\r\n' +
    'DEVICE: ' +
    si.device +
    '\r\n'
  );
};
